import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Weight, Layers, Box } from "lucide-react";
import type { VirtualPalletDto, PhysicalPalletDto } from "@/types/loading-plan";
import { Pallet3DViewer } from "./Pallet3DViewer";
import { PALLET_3D_PREVIEW_ENABLED } from "@/constants/featureFlags";

interface PalletCardProps {
  pallet: VirtualPalletDto | PhysicalPalletDto;
  type: "virtual" | "physical";
  onConvert?: (palletId: string) => void;
}

export function PalletCard({ pallet, type, onConvert }: PalletCardProps) {
  const [show3D, setShow3D] = React.useState(false);
  const isPhysical = type === "physical";
  const physicalPallet = isPhysical ? (pallet as PhysicalPalletDto) : null;

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="h-5 w-5" />
            {isPhysical ? (
              <span>Palet {physicalPallet?.palletCode}</span>
            ) : (
              <span>
                Palet Virtual #{(pallet as VirtualPalletDto).sequenceNumber}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {PALLET_3D_PREVIEW_ENABLED && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShow3D(true);
                }}
                className="flex items-center gap-1 px-2 py-1 hover:bg-blue-50 bg-white rounded-md transition-colors mr-2 border border-slate-200 shadow-sm"
                title="Ver modelo 3D"
              >
                <Box className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-700">VER 3D</span>
              </button>
            )}
            <Badge variant={isPhysical ? "default" : "secondary"}>
              {isPhysical ? physicalPallet?.status : "Virtual"}
            </Badge>
            <Badge variant="outline">Orden: {pallet.loadingOrder}</Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Métricas del palet */}
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col items-center p-3 bg-muted rounded-lg">
            <Package className="h-4 w-4 text-muted-foreground mb-1" />
            <span className="text-2xl font-bold">{pallet.totalBoxes}</span>
            <span className="text-xs text-muted-foreground">Cajas</span>
          </div>

          <div className="flex flex-col items-center p-3 bg-muted rounded-lg">
            <Weight className="h-4 w-4 text-muted-foreground mb-1" />
            <span className="text-2xl font-bold">
              {isPhysical
                ? physicalPallet?.actualWeightKg.toFixed(1)
                : (pallet as VirtualPalletDto).totalWeightKg.toFixed(1)}
            </span>
            <span className="text-xs text-muted-foreground">kg</span>
          </div>

          {!isPhysical && (
            <div className="flex flex-col items-center p-3 bg-muted rounded-lg">
              <Layers className="h-4 w-4 text-muted-foreground mb-1" />
              <span className="text-2xl font-bold">
                {(pallet as VirtualPalletDto).estimatedHeightCm.toFixed(0)}
              </span>
              <span className="text-xs text-muted-foreground">cm</span>
            </div>
          )}
        </div>

        {/* Pedidos / Clientes */}
        {pallet.customerNames && pallet.customerNames.length > 0 && (
          <div className="space-y-2 pb-2 border-b border-border">
            <h4 className="text-sm font-semibold text-muted-foreground">
              Pedidos:
            </h4>
            <div className="flex flex-col gap-1.5">
              {pallet.customerNames.map((name, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="font-mono text-xs shrink-0 bg-muted"
                  >
                    #{(pallet.workOrderIds?.[idx] ?? "").slice(-6).toUpperCase() || idx + 1}
                  </Badge>
                  <span className="text-sm text-blue-700 font-medium truncate">
                    {name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lista de productos */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-muted-foreground">
            Contenido:
          </h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {pallet.items.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm"
              >
                <div className="flex-1">
                  <span className="font-medium">{item.productName}</span>
                  <span className="text-muted-foreground ml-2">
                    Nivel {item.stackingLevel}
                  </span>
                </div>
                <div className="flex gap-3 text-muted-foreground">
                  <span>
                    {item.numberOfBoxes} {item.packagingName || "cajas"}
                  </span>
                  <span>{item.weightKg.toFixed(1)} kg</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Botón de conversión para palets virtuales */}
        {!isPhysical && onConvert && (
          <button
            onClick={() => onConvert(pallet.id)}
            className="w-full mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Convertir a Físico
          </button>
        )}

        {show3D && (
          <Pallet3DViewer
            palletId={pallet.id}
            type={isPhysical ? "physical" : "virtual"}
            isOpen={show3D}
            onClose={() => setShow3D(false)}
            palletNumber={
              isPhysical
                ? (pallet as PhysicalPalletDto).palletCode
                : (pallet as VirtualPalletDto).sequenceNumber
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
